// To-scale dimensioned elevation of a parametric panel — auto-generated, no drawing needed.
import { getLaserPreset } from "@/lib/laserCutPresets";

export const PanelPreview = ({ panel, layout, colourHex = "#334155" }) => {
  const W = 420, H = 260;
  const p = panel, L = layout || {};
  if (p.kind === "box") return <BoxPreview panel={p} colourHex={colourHex} />;

  const spike = p.infill_type === "tube_spike";
  const ext = spike ? (p.spike_extension_mm || 0) : 0;
  const scale = Math.min((W - 90) / p.width_mm, (H - 70) / (p.height_mm + ext));
  const pw = p.width_mm * scale, ph = p.height_mm * scale, pext = ext * scale;
  const x0 = 55, y0 = 30 + pext;
  const post = Math.max(3, (L.post_size || 65) * scale);
  const rail = Math.max(2.5, (L.rail_size || 40) * scale);
  const tube = Math.max(1.5, (L.tube_size || 25) * scale);
  const clearW = pw - 2 * post;
  const n = L.n_infill || 0;

  const infill = [];
  const patternId = `pat-${p.laser_cut_preset || "default"}`;
  if ((p.infill_type === "tube_spike" || p.infill_type === "tube_flat") && n > 1) {
    for (let i = 0; i < n; i++) {
      const cx = x0 + post + (i * (clearW - tube)) / (n - 1);
      const topY = spike ? y0 - pext : y0 + rail;
      infill.push(<rect key={`t${i}`} x={cx} y={topY} width={tube} height={y0 + ph - rail - topY} fill={colourHex} />);
      if (spike) infill.push(<polygon key={`s${i}`} points={`${cx - tube * 0.6},${topY} ${cx + tube * 1.6},${topY} ${cx + tube / 2},${topY - 7}`} fill={colourHex} />);
    }
  } else if (p.infill_type === "slat" && n > 0) {
    const innerH = ph - 2 * rail;
    for (let i = 0; i < n; i++) {
      const sy = y0 + rail + (i * innerH) / n;
      infill.push(<rect key={`sl${i}`} x={x0 + post} y={sy} width={clearW} height={Math.max(2, innerH / n * 0.75)} fill={colourHex} />);
    }
  } else if (p.infill_type === "louvre" && n > 0) {
    const innerH = ph - 2 * rail;
    const angle = p.louvre_angle_deg || 45;
    const projW = Math.max(2, (p.louvre_width_mm || 100) * scale * Math.cos((angle * Math.PI) / 180));
    for (let i = 0; i < n; i++) {
      const sy = y0 + rail + (i + 0.5) * (innerH / n);
      const cx = x0 + post + clearW / 2;
      infill.push(
        <g key={`lv${i}`} transform={`rotate(${-angle} ${cx} ${sy})`}>
          <rect x={x0 + post} y={sy - projW / 2} width={clearW} height={projW} fill={colourHex} rx={projW / 3} />
        </g>
      );
    }
    // side brackets showing slot positions
    infill.push(<rect key="lbL" x={x0 + post - 1} y={y0 + rail} width={2} height={innerH} fill={colourHex} opacity={0.35} />);
    infill.push(<rect key="lbR" x={x0 + pw - post - 1} y={y0 + rail} width={2} height={innerH} fill={colourHex} opacity={0.35} />);
  } else if (p.infill_type === "blade") {
    const orient = p.blade_orientation || "vertical";
    const bw = Math.max(1.5, (p.blade_width_mm || 50) * scale);
    if (orient === "horizontal") {
      const innerH = ph - 2 * rail;
      const nB = Math.max(1, Math.floor((p.height_mm - 2 * (L.rail_size || 40) + (p.blade_gap_mm || 20)) / ((p.blade_width_mm || 50) + (p.blade_gap_mm || 20))));
      const pitch = innerH / nB;
      for (let i = 0; i < nB; i++) {
        infill.push(<rect key={`bh${i}`} x={x0 + post} y={y0 + rail + i * pitch + (pitch - bw) / 2} width={clearW} height={bw} fill={colourHex} />);
      }
    } else {
      const nB = Math.max(1, Math.floor((p.width_mm - 2 * (L.post_size || 65) + (p.blade_gap_mm || 20)) / ((p.blade_width_mm || 50) + (p.blade_gap_mm || 20))));
      const pitch = clearW / nB;
      for (let i = 0; i < nB; i++) {
        infill.push(<rect key={`bv${i}`} x={x0 + post + i * pitch + (pitch - bw) / 2} y={y0 + rail} width={bw} height={ph - 2 * rail} fill={colourHex} />);
      }
    }
  } else if (p.infill_type === "laser_cut") {
    const preset = getLaserPreset(p.laser_cut_preset);
    const tilePx = Math.max(30, (preset.tile_mm || 200) * scale);
    infill.push(
      <defs key="defs">
        <pattern id={patternId} x="0" y="0" width={tilePx} height={tilePx} patternUnits="userSpaceOnUse">
          {p.laser_cut_preset === "custom" && p.laser_cut_upload_url ? (
            <image href={p.laser_cut_upload_url} x="0" y="0" width={tilePx} height={tilePx} preserveAspectRatio="xMidYMid slice" />
          ) : (
            <g transform={`scale(${tilePx / 100})`} dangerouslySetInnerHTML={{ __html: preset.render(colourHex) }} />
          )}
        </pattern>
      </defs>
    );
    infill.push(<rect key="lc-bg" x={x0 + post} y={y0 + rail} width={clearW} height={ph - 2 * rail} fill="#f8fafc" stroke={colourHex} strokeWidth={0.8} />);
    infill.push(<rect key="lc-fill" x={x0 + post} y={y0 + rail} width={clearW} height={ph - 2 * rail} fill={`url(#${patternId})`} />);
  } else if (["rolltop", "anticlimb", "mesh"].includes(p.infill_type)) {
    const step = p.infill_type === "anticlimb" ? 5 : 11;
    for (let x = x0 + post; x < x0 + pw - post; x += step) infill.push(<line key={`mv${x}`} x1={x} y1={y0 + 2} x2={x} y2={y0 + ph - 2} stroke={colourHex} strokeWidth={0.7} />);
    for (let y = y0 + 4; y < y0 + ph; y += step + 4) infill.push(<line key={`mh${y}`} x1={x0 + post} y1={y} x2={x0 + pw - post} y2={y} stroke={colourHex} strokeWidth={0.7} />);
    if (p.infill_type === "rolltop") infill.push(<path key="rt" d={`M ${x0 + post} ${y0 + 10} Q ${x0 + pw / 2} ${y0 - 8} ${x0 + pw - post} ${y0 + 10}`} fill="none" stroke={colourHex} strokeWidth={3} />);
  } else if (p.infill_type === "sheet") {
    infill.push(<rect key="sh" x={x0 + post} y={y0 + rail} width={clearW} height={ph - 2 * rail} fill={colourHex} opacity={0.85} />);
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-h-72 bg-white rounded-sm border border-slate-200" data-testid="panel-preview">
      {infill}
      <rect x={x0} y={y0} width={post} height={ph} fill={colourHex} />
      <rect x={x0 + pw - post} y={y0} width={post} height={ph} fill={colourHex} />
      <rect x={x0 + post} y={y0} width={clearW} height={rail} fill={colourHex} />
      <rect x={x0 + post} y={y0 + ph - rail} width={clearW} height={rail} fill={colourHex} />
      {Array.from({ length: parseInt(p.mid_rails) || 0 }).map((_, i) => (
        <rect key={`mr${i}`} x={x0 + post} y={y0 + ((i + 1) * ph) / ((parseInt(p.mid_rails) || 0) + 1) - rail / 2} width={clearW} height={rail} fill={colourHex} />
      ))}
      {/* dimensions */}
      <line x1={x0} y1={y0 + ph + 14} x2={x0 + pw} y2={y0 + ph + 14} stroke="#0e7490" strokeWidth={1} markerStart="url(#arr)" markerEnd="url(#arr)" />
      <text x={x0 + pw / 2} y={y0 + ph + 26} fontSize={10} fontFamily="monospace" fill="#0e7490" textAnchor="middle" fontWeight="bold">{`${p.width_mm} mm`}</text>
      <line x1={x0 - 14} y1={y0} x2={x0 - 14} y2={y0 + ph} stroke="#0e7490" strokeWidth={1} />
      <text x={x0 - 20} y={y0 + ph / 2} fontSize={10} fontFamily="monospace" fill="#0e7490" textAnchor="middle" fontWeight="bold" transform={`rotate(-90 ${x0 - 20} ${y0 + ph / 2})`}>{`${p.height_mm} mm`}</text>
      {n > 1 && (p.infill_type === "tube_spike" || p.infill_type === "tube_flat") && (
        <text x={x0 + pw / 2} y={y0 - pext - 8} fontSize={9} fontFamily="monospace" fill="#b45309" textAnchor="middle">{`${L.n_infill} × ${p.tube_profile} @ ${L.actual_centres_mm} mm c/c${spike ? ` · spikes +${p.spike_extension_mm} mm` : ""}`}</text>
      )}
    </svg>
  );
};

const BoxPreview = ({ panel: p, colourHex }) => {
  const W = 420, H = 260;
  const scale = Math.min(200 / (p.width_mm + p.depth_mm * 0.5), 170 / (p.height_mm + p.depth_mm * 0.3));
  const bw = p.width_mm * scale, bh = p.height_mm * scale, bd = p.depth_mm * scale * 0.5;
  const x0 = 90, y0 = 50 + bd * 0.6;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full bg-white rounded-sm border border-slate-200" data-testid="panel-preview">
      <polygon points={`${x0},${y0} ${x0 + bd},${y0 - bd * 0.6} ${x0 + bd + bw},${y0 - bd * 0.6} ${x0 + bw},${y0}`} fill={colourHex} opacity={0.55} />
      <polygon points={`${x0 + bw},${y0} ${x0 + bd + bw},${y0 - bd * 0.6} ${x0 + bd + bw},${y0 - bd * 0.6 + bh} ${x0 + bw},${y0 + bh}`} fill={colourHex} opacity={0.75} />
      <rect x={x0} y={y0} width={bw} height={bh} fill={colourHex} />
      <rect x={x0 + bw * 0.12} y={y0 + bh * 0.12} width={bw * 0.76} height={bh * 0.76} fill="none" stroke="#ffffff" strokeWidth={1.5} strokeDasharray="4 3" />
      <circle cx={x0 + bw * 0.82} cy={y0 + bh * 0.5} r={2.5} fill="#ffffff" />
      <line x1={x0} y1={y0 + bh + 14} x2={x0 + bw} y2={y0 + bh + 14} stroke="#0e7490" strokeWidth={1} />
      <text x={x0 + bw / 2} y={y0 + bh + 26} fontSize={10} fontFamily="monospace" fill="#0e7490" textAnchor="middle" fontWeight="bold">{`${p.width_mm} mm`}</text>
      <text x={x0 - 20} y={y0 + bh / 2} fontSize={10} fontFamily="monospace" fill="#0e7490" textAnchor="middle" fontWeight="bold" transform={`rotate(-90 ${x0 - 20} ${y0 + bh / 2})`}>{`${p.height_mm} mm`}</text>
      <text x={x0 + bw + bd / 2 + 6} y={y0 - bd * 0.6 + bh + 14} fontSize={9} fontFamily="monospace" fill="#0e7490">{`${p.depth_mm} mm deep`}</text>
      <text x={x0 + bw / 2} y={y0 + bh * 0.55} fontSize={9} fill="#ffffff" textAnchor="middle">weatherproof door</text>
    </svg>
  );
};

export default PanelPreview;
